// FILE: composerQueueStore.ts
// Purpose: Client mirror of the server-side composer queue — the messages someone wrote while an
//   agent was mid-turn, kept per thread and owned by their author.
// Layer: Web state (zustand, in memory)
// Depends on: the native API bridge (`composerQueue.*` RPC) and the `composerQueue.changed` push.
//
// Nothing here is persisted. The server owns the queue: every mutation returns the authoritative
// list for the thread and that list replaces whatever was in memory, so two tabs of the same
// person converge without any merge logic. The `composerQueue.changed` push only names the
// thread, so it triggers a reload rather than carrying rows — the same channel fires for other
// people's rows in the same thread, and those must never appear here.
import type {
  ComposerQueueEnqueueInput,
  ComposerQueueSendResult,
  ComposerQueuedMessage,
  ThreadId,
  TurnDispatchMode,
} from "@taskin-code/contracts";
import { create } from "zustand";

import { readNativeApi } from "./nativeApi";
import { onComposerQueueChanged } from "./wsNativeApi";

export type ComposerQueueEntryStatus = "idle" | "loading" | "loaded" | "error";

export interface ComposerQueueEntry {
  readonly status: ComposerQueueEntryStatus;
  readonly items: ReadonlyArray<ComposerQueuedMessage>;
  /** Error from the last load or mutation, shown inline in the popover. */
  readonly error: string | null;
  /** Queued ids with a mutation in flight, so their row can disable its controls. */
  readonly pendingIds: ReadonlyArray<string>;
}

export const EMPTY_COMPOSER_QUEUE_ENTRY: ComposerQueueEntry = Object.freeze({
  status: "idle" as const,
  items: Object.freeze([]) as ReadonlyArray<ComposerQueuedMessage>,
  error: null,
  pendingIds: Object.freeze([]) as ReadonlyArray<string>,
});

interface ComposerQueueStoreState {
  readonly byThreadId: Record<string, ComposerQueueEntry>;
  readonly load: (threadId: ThreadId) => Promise<void>;
  readonly enqueue: (input: ComposerQueueEnqueueInput) => Promise<void>;
  readonly setAutoSend: (threadId: ThreadId, queuedId: string, autoSend: boolean) => Promise<void>;
  readonly remove: (threadId: ThreadId, queuedId: string) => Promise<void>;
  readonly send: (
    threadId: ThreadId,
    queuedId: string,
    dispatchMode?: TurnDispatchMode,
  ) => Promise<ComposerQueueSendResult>;
  readonly applyChanged: (payload: { readonly threadId: ThreadId }) => void;
  readonly clear: (threadId: ThreadId) => void;
}

const toMessage = (cause: unknown): string =>
  cause instanceof Error ? cause.message : String(cause);

/**
 * The native API is absent until the transport connects. Throwing funnels that into the same
 * error slot as a failed request, so the popover says "not connected" instead of doing nothing.
 */
const requireApi = () => {
  const api = readNativeApi();
  if (api === undefined) throw new Error("Not connected to the server");
  return api.composerQueue;
};

export const useComposerQueueStore = create<ComposerQueueStoreState>((set, get) => {
  const patch = (threadId: ThreadId, changes: Partial<ComposerQueueEntry>): void => {
    set((state) => ({
      byThreadId: {
        ...state.byThreadId,
        [threadId]: { ...(state.byThreadId[threadId] ?? EMPTY_COMPOSER_QUEUE_ENTRY), ...changes },
      },
    }));
  };

  const markPending = (threadId: ThreadId, queuedId: string, pending: boolean): void => {
    const entry = get().byThreadId[threadId] ?? EMPTY_COMPOSER_QUEUE_ENTRY;
    const pendingIds = pending
      ? entry.pendingIds.includes(queuedId)
        ? entry.pendingIds
        : [...entry.pendingIds, queuedId]
      : entry.pendingIds.filter((id) => id !== queuedId);
    patch(threadId, { pendingIds });
  };

  // Mutations answer with the whole list for the thread, so the response is the state.
  const applyItems = (threadId: ThreadId, items: ReadonlyArray<ComposerQueuedMessage>): void => {
    patch(threadId, { status: "loaded", items, error: null });
  };

  /**
   * Mutations record the failure on the entry *and* rethrow: the popover shows it inline while
   * the caller (the composer) still learns the message was not queued and keeps the draft.
   */
  const runMutation = async <T>(
    threadId: ThreadId,
    queuedId: string | null,
    run: () => Promise<T>,
  ): Promise<T> => {
    if (queuedId !== null) markPending(threadId, queuedId, true);
    try {
      const result = await run();
      return result;
    } catch (cause) {
      patch(threadId, { error: toMessage(cause) });
      throw cause;
    } finally {
      if (queuedId !== null) markPending(threadId, queuedId, false);
    }
  };

  return {
    byThreadId: {},

    load: async (threadId) => {
      const entry = get().byThreadId[threadId];
      patch(threadId, { status: entry?.status === "loaded" ? "loaded" : "loading", error: null });
      try {
        const result = await requireApi().list({ threadId });
        applyItems(threadId, result.items);
      } catch (cause) {
        patch(threadId, { status: "error", error: toMessage(cause) });
      }
    },

    enqueue: async (input) => {
      const result = await runMutation(input.threadId, null, () => requireApi().enqueue(input));
      applyItems(input.threadId, result.items);
    },

    setAutoSend: async (threadId, queuedId, autoSend) => {
      const result = await runMutation(threadId, queuedId, () =>
        requireApi().update({ queuedId, autoSend }),
      );
      applyItems(threadId, result.items);
    },

    remove: async (threadId, queuedId) => {
      const result = await runMutation(threadId, queuedId, () => requireApi().remove({ queuedId }));
      applyItems(threadId, result.items);
    },

    send: async (threadId, queuedId, dispatchMode) => {
      const result = await runMutation(threadId, queuedId, () =>
        requireApi().send({ queuedId, ...(dispatchMode ? { dispatchMode } : {}) }),
      );
      applyItems(threadId, result.items);
      if (result.outcome === "failed") {
        patch(threadId, { error: result.message ?? "Could not send the queued message." });
      }
      return result;
    },

    // The push carries no rows, so it can only mean "re-read". Threads nobody has opened are
    // skipped: loading them here would fetch queues no view is showing.
    applyChanged: ({ threadId }) => {
      if (get().byThreadId[threadId] === undefined) return;
      void get().load(threadId);
    },

    clear: (threadId) => {
      set((state) => {
        if (!(threadId in state.byThreadId)) return state;
        const byThreadId = { ...state.byThreadId };
        delete byThreadId[threadId];
        return { byThreadId };
      });
    },
  };
});

// Registered on import rather than from `wsNativeApi`: the store depends on the transport, not
// the other way round, so the module graph stays acyclic.
onComposerQueueChanged((payload) => {
  useComposerQueueStore.getState().applyChanged(payload);
});

export const selectComposerQueueEntry = (
  state: ComposerQueueStoreState,
  threadId: ThreadId | null,
): ComposerQueueEntry =>
  threadId === null
    ? EMPTY_COMPOSER_QUEUE_ENTRY
    : (state.byThreadId[threadId] ?? EMPTY_COMPOSER_QUEUE_ENTRY);
